/**
 * Book an appointment manually against a lead.
 *
 * POST /v1/appointments
 *   { type, leadId, customerId, vehicleId, consultantId, startsAt, endsAt,
 *     location, notes, status, source, force, notifyCustomer, notifyAssignee }
 *
 * The backend conflict-checks the consultant's diary and answers
 * APPOINTMENT_CONFLICT unless force=true — we surface that as an explicit
 * "book anyway (double-book)" confirmation rather than a dead error.
 *
 * notifyCustomer/notifyAssignee (T086) mirror CompleteTaskModal's inline
 * booking checkboxes — this was the one booking path that didn't have them,
 * which meant every appointment made here always texted the customer with no
 * way to opt out. Defaulting notifyCustomer to true keeps today's behaviour
 * for anyone who doesn't touch the checkbox.
 */
import LookupField from './LookupField';
import { useVehicleOptions } from '../lib/useDirectory';
import { useEffect, useMemo, useState } from 'react';
import SlotPicker, { todayISO } from './SlotPicker';
import { appointmentsService } from '../lib/services';
import { apiErrorMessage, apiErrorCode } from '../lib/apiError';

/**
 * Appointment types a SALES consultant can book. (E2 / T023, Ajay #2.)
 *
 * Service is deliberately absent. This modal opens from the lead detail and
 * the deal detail — both sales surfaces, both booking against a sales lead —
 * and the list used to offer Service anyway. Picking it wrote a SERVICE
 * appointment hung off a sales lead, which is where mixed data came from in
 * the first place: every report downstream then had to remember to filter
 * something that should never have been created here.
 *
 * Segregating the reports without segregating the booking form would have been
 * treating the symptom. Service bookings belong to the workshop's own flow.
 */
const TYPES = [
  ['TEST_DRIVE',      'Test drive'],
  ['SHOWROOM_VISIT',  'Showroom visit'],
  ['APPRAISAL',       'Trade-in appraisal'],
  ['FINANCE_CONSULT', 'Finance consultation'],
  ['DELIVERY',        'Delivery'],
  ['FOLLOW_UP_CALL',  'Follow-up call'],
];

export default function BookAppointmentModal({ open, lead, customer, onClose, onBooked }) {
  const [type, setType]         = useState('TEST_DRIVE');
  const [date, setDate]         = useState(todayISO());
  const [slot, setSlot]         = useState(null);
  const [location, setLocation] = useState('');
  const [notes, setNotes]       = useState('');
  // (no "confirm now" toggle any more — a staff member picking a slot has
  // already confirmed by clicking it. The backend defaults a HUMAN booking to
  // CONFIRMED and an AI-agent one to REQUESTED, so this modal and the two on
  // Appointments / Test Drives all land in the same state without a toggle
  // that only one of them ever offered.)
  // null = untouched (defaults apply) · '' = deliberately cleared · uuid = chosen
  const [vehiclePick, setVehiclePick] = useState(null);
  // TEST_DRIVE → demo cars only (matches Appointments/Test Drives modals);
  // any other appointment type still sees every vehicle, because a Delivery
  // or Finance visit points at the customer's actual ordered car, not a demo.
  const vehiclesQ = useVehicleOptions(
    type === 'TEST_DRIVE' ? { condition: 'DEMO' } : {}
  );

  /**
   * Pre-match the stock vehicle from the lead's enquiry.
   *
   * Most leads carry their car as typed text ("Mitsubishi Triton"), not a
   * stock link — which is why test drives booked here used to reach the
   * Test Drives board with Vehicle "—". When exactly one stock unit matches
   * the enquiry text, offer it pre-selected; the consultant can clear or
   * change it. Zero or several matches → leave the choice to the human.
   */
  const enquiryMatch = useMemo(() => {
    if (lead?.vehicleOfInterestId) return null;               // already linked
    const text = String(lead?.vehicle || '').trim().toLowerCase();
    if (!text || text === '—') return null;
    const hits = vehiclesQ.options.filter((o) =>
      (o.search ?? `${o.primary ?? ''} ${o.secondary ?? ''}`).toLowerCase()
        .includes(text.split(' ').pop()));                    // match on model word
    return hits.length === 1 ? hits[0] : null;
  }, [lead, vehiclesQ.options]);
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [notifyAssignee, setNotifyAssignee] = useState(true);
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState(null);
  const [conflict, setConflict] = useState(false);   // offer force-book

  // Reset the form each time the modal OPENS — and only then. This must depend
  // on `open` alone: the parents pass an inline `onClose={() => setBookOpen(false)}`
  // whose identity changes on every re-render, and LeadDetail re-renders every
  // 3s from its polling loop. If `onClose` were in these deps, each poll would
  // re-run this effect and blank out the slot the user just picked.
  useEffect(() => {
    if (!open) return;
    setType('TEST_DRIVE');
    setVehiclePick(null);
    setDate(todayISO());
    setSlot(null);
    setLocation('');
    setNotes('');
    setNotifyCustomer(true);
    setNotifyAssignee(true);
    setBusy(false);
    setError(null);
    setConflict(false);
  }, [open]);

  /** What actually rides on the booking: explicit pick > lead link > enquiry match. */
  const effectiveVehicleId =
    vehiclePick === '' ? null
      : (vehiclePick ?? lead?.vehicleOfInterestId ?? enquiryMatch?.id ?? null);

  // Close on Escape. Kept separate so re-registering the listener when the
  // parent hands us a new `onClose` never touches form state.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !lead) return null;

  const book = async (force) => {
    if (!slot) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await appointmentsService.create({
        type,
        leadId:       lead.id,
        customerId:   lead.customerId || customer?.id || null,
        vehicleId:    effectiveVehicleId,
        consultantId: slot.consultantId,
        startsAt:     slot.startsAt,
        endsAt:       slot.endsAt,
        location:     location.trim() || null,
        notes:        notes.trim() || null,
        // Let the backend set the default (HUMAN → CONFIRMED here).
        source:       'HUMAN',
        notifyCustomer,
        notifyAssignee,
        ...(force ? { force: true } : {}),
      });
      onBooked?.(saved);
      onClose?.();
    } catch (err) {
      if (apiErrorCode(err) === 'APPOINTMENT_CONFLICT') {
        setConflict(true);
        setError('That consultant already has an appointment overlapping this slot.');
      } else {
        setError(apiErrorMessage(err, 'Could not create the appointment'));
      }
      setBusy(false);
    }
  };

  const submit = (e) => { e.preventDefault(); book(false); };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="bam-title"
         onClick={onClose} className="modal-backdrop">
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}
            style={{ width: 'min(560px, 92vw)' }}>
        <div className="modal-header">
          <div>
            <div id="bam-title" style={{ fontSize: 16, fontWeight: 600 }}>Book appointment</div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
              {customer?.name || 'this lead'} · {lead.vehicle || 'no vehicle of interest'}
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-field">
            <label htmlFor="bam-type">Type</label>
            <select
              id="bam-type"
              value={type}
              onChange={(e) => {
                // See Appointments.jsx: type change reshuffles the stock list
                // and the slot list, so clear a pick that may not survive it.
                setType(e.target.value);
                setSlot(null);
                setVehiclePick('');
              }}
              disabled={busy}
              autoFocus
            >
              {TYPES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </div>

          <div className="form-field">
            <LookupField
              label="Vehicle (optional)"
              placeholder="Search stock — attach at check-in if unsure"
              options={vehiclesQ.options}
              loading={vehiclesQ.loading}
              value={effectiveVehicleId}
              onChange={(id) => setVehiclePick(id ?? '')}
              disabled={busy}
            />
            {effectiveVehicleId && enquiryMatch && effectiveVehicleId === enquiryMatch.id && vehiclePick === null && (
              <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>
                Matched from the enquiry “{lead.vehicle}” — clear it if that’s not the car.
              </div>
            )}
          </div>

          <SlotPicker
            date={date}
            onDateChange={(d) => { setDate(d); setSlot(null); setConflict(false); }}
            type={type}
            value={slot}
            onSelect={(s) => { setSlot(s); setConflict(false); setError(null); }}
            disabled={busy}
          />

          <div className="form-field">
            <label htmlFor="bam-location">Location</label>
            <input
              id="bam-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Showroom floor, customer's address…"
              disabled={busy}
            />
          </div>

          <div className="form-field">
            <label htmlFor="bam-notes">Notes</label>
            <textarea
              id="bam-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything the consultant should know"
              disabled={busy}
            />
          </div>

          <div className="flex-row" style={{ gap: 16, flexWrap: 'wrap' }}>
            <label className="flex-row" style={{ alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notifyCustomer}
                onChange={(e) => setNotifyCustomer(e.target.checked)}
                disabled={busy}
              />
              <span>Notify customer</span>
            </label>
            <label className="flex-row" style={{ alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notifyAssignee}
                onChange={(e) => setNotifyAssignee(e.target.checked)}
                disabled={busy}
              />
              <span>Notify assignee</span>
            </label>
          </div>

          {error && (
            <div className="text-xs" style={{ color: conflict ? 'var(--warning)' : 'var(--danger)', lineHeight: 1.6 }}>
              {error}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose} disabled={busy}>Cancel</button>
          {conflict ? (
            <button type="button" className="btn small danger" onClick={() => book(true)} disabled={busy}>
              {busy ? 'Booking…' : 'Book anyway (double-book)'}
            </button>
          ) : (
            <button type="submit" className="btn small primary" disabled={busy || !slot}>
              {busy ? 'Booking…' : 'Book appointment'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
